"use client";

import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { inputVariants } from "./input";
import { Button } from "./button";
import {
  MONTH_PICKER_MONTHS,
  MONTH_PICKER_OPTION_CLASS_NAME,
  MONTH_PICKER_SELECTED_CLASS_NAME,
  MONTH_PICKER_YEARS_PER_PAGE,
} from "./month-picker.constants";
import {
  formatMonthValue,
  getMonthPickerBounds,
  getMonthPickerYear,
  toMonthValue,
} from "./month-picker.utils";
import type { MonthPickerProps } from "./month-picker.types";

export function MonthPicker({
  id,
  label,
  value,
  onChange,
  min,
  max,
  disabled,
  className,
}: MonthPickerProps) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => getMonthPickerYear(value));
  const [showYears, setShowYears] = useState(false);
  const calendar = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const selected = toMonthValue(value);
  const { minimum, maximum, minYear, maxYear } = getMonthPickerBounds(min, max);
  const firstYear =
    Math.floor((year - 1) / MONTH_PICKER_YEARS_PER_PAGE) *
      MONTH_PICKER_YEARS_PER_PAGE +
    1;
  const lastYear = firstYear + MONTH_PICKER_YEARS_PER_PAGE - 1;
  const select = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="relative">
      <DialogPrimitive.Root
        modal={false}
        open={open && !disabled}
        onOpenChange={(next) => {
          if (next) {
            setYear(
              Math.min(maxYear, Math.max(minYear, getMonthPickerYear(value))),
            );
            setShowYears(false);
          }
          setOpen(next);
        }}
      >
        <DialogPrimitive.Trigger asChild>
          <button
            id={id}
            type="button"
            aria-label={label}
            disabled={disabled}
            className={cn(
              inputVariants(),
              "cursor-pointer items-center justify-between gap-2 text-left",
              className,
            )}
          >
            <span className={cn("truncate", !value && "text-gray-400")}>
              {formatMonthValue(value) || "Select month and year"}
            </span>
            <CalendarDays
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-[#667085]"
            />
          </button>
        </DialogPrimitive.Trigger>
        {open && !disabled ? (
          <DialogPrimitive.Content
            ref={calendar}
            aria-describedby={undefined}
            className="absolute left-0 top-full z-30 mt-1 max-h-64 w-64 max-w-full overflow-y-auto overscroll-contain rounded-lg border border-black bg-white p-2 shadow-[0_2px_0_0_#000000] outline-none"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              calendar.current?.scrollIntoView({ block: "nearest" });
              (
                grid.current?.querySelector<HTMLButtonElement>(
                  'button[aria-pressed="true"]:not(:disabled)',
                ) ||
                grid.current?.querySelector<HTMLButtonElement>(
                  "button:not(:disabled)",
                )
              )?.focus();
            }}
          >
            <DialogPrimitive.Title className="sr-only">
              {label}
            </DialogPrimitive.Title>
            <div className="mb-1 flex items-center justify-between gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={showYears ? "Previous years" : "Previous year"}
                disabled={(showYears ? firstYear : year) <= minYear}
                onClick={() =>
                  setYear(
                    Math.max(
                      minYear,
                      year - (showYears ? MONTH_PICKER_YEARS_PER_PAGE : 1),
                    ),
                  )
                }
              >
                <ChevronLeft aria-hidden="true" className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-7 gap-1 px-2 text-xs"
                aria-label={showYears ? "Choose month" : "Choose year"}
                onClick={() => setShowYears(!showYears)}
              >
                {showYears
                  ? `${firstYear} - ${Math.min(lastYear, maxYear)}`
                  : year}
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={showYears ? "Next years" : "Next year"}
                disabled={(showYears ? lastYear : year) >= maxYear}
                onClick={() =>
                  setYear(
                    Math.min(
                      maxYear,
                      year + (showYears ? MONTH_PICKER_YEARS_PER_PAGE : 1),
                    ),
                  )
                }
              >
                <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div
              ref={grid}
              role="group"
              aria-label={showYears ? "Years" : `Months in ${year}`}
              className="grid grid-cols-3 gap-1"
            >
              {showYears
                ? Array.from(
                    { length: MONTH_PICKER_YEARS_PER_PAGE },
                    (_, index) => {
                      const option = firstYear + index;
                      return (
                        <button
                          type="button"
                          key={option}
                          disabled={option < minYear || option > maxYear}
                          aria-pressed={option === year}
                          className={cn(
                            MONTH_PICKER_OPTION_CLASS_NAME,
                            option === year && MONTH_PICKER_SELECTED_CLASS_NAME,
                          )}
                          onClick={() => {
                            setYear(option);
                            setShowYears(false);
                          }}
                        >
                          {option}
                        </button>
                      );
                    },
                  )
                : MONTH_PICKER_MONTHS.map((month, index) => {
                    const option = `${String(year).padStart(4, "0")}-${String(index + 1).padStart(2, "0")}`;
                    return (
                      <button
                        type="button"
                        key={month}
                        aria-label={`${month} ${year}`}
                        aria-pressed={option === selected}
                        disabled={Boolean(
                          (minimum && option < minimum) ||
                          (maximum && option > maximum),
                        )}
                        className={cn(
                          MONTH_PICKER_OPTION_CLASS_NAME,
                          option === selected &&
                            MONTH_PICKER_SELECTED_CLASS_NAME,
                        )}
                        onClick={() => select(option)}
                      >
                        {month.slice(0, 3)}
                      </button>
                    );
                  })}
            </div>
            <div className="mt-1 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={!value}
                onClick={() => select("")}
              >
                Clear date
              </Button>
            </div>
          </DialogPrimitive.Content>
        ) : null}
      </DialogPrimitive.Root>
    </div>
  );
}
